import { createContext, useContext } from 'react';
import type { UserProfile } from '@pay888/shared';
import type { Unsubscribe, User } from 'firebase/auth';

export interface AuthDependencies {
  readonly observe: (listener: (user: User | null) => void) => Unsubscribe;
  readonly signIn: (email: string, password: string) => Promise<User>;
  readonly signUp: (email: string, password: string) => Promise<User>;
  readonly signInWithGooglePopup: () => Promise<User>;
  readonly signInWithApplePopup: () => Promise<User>;
  readonly updateDisplayName: (user: User, displayName: string) => Promise<void>;
  readonly sendEmailVerification: (user: User) => Promise<void>;
  readonly reloadUser: (user: User) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly sendPasswordReset: (email: string) => Promise<void>;
  readonly ensureProfile: () => Promise<unknown>;
  readonly hasAdminClaim: (user: User) => Promise<boolean>;
}

export interface AuthContextValue {
  readonly currentUser: AuthUser | null;
  readonly userProfile: UserProfile | null;
  readonly isAdmin: boolean;
  readonly isLoading: boolean;
  readonly isConfigured: boolean;
  readonly signInWithEmail: (email: string, password: string) => Promise<void>;
  readonly signUpWithEmail: (displayName: string, email: string, password: string) => Promise<void>;
  readonly signInWithGoogle: () => Promise<void>;
  readonly signInWithApple: () => Promise<void>;
  readonly updateUserDisplayName: (displayName: string) => Promise<void>;
  readonly sendVerificationEmail: () => Promise<void>;
  readonly refreshUserVerificationStatus: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly sendPasswordReset: (email: string) => Promise<void>;
}

export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly emailVerified: boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}
